// A progress bar for route changes that take long enough to look broken.
//
// Gatsby swaps the page only once that route's data and JavaScript have
// arrived. On a fast connection that is imperceptible; on a slow one the
// address bar changes immediately and the old page — with the old tab still
// highlighted — stays put for several seconds with nothing to say why. That is
// the "clicking a tab does nothing" report, and it is worst on exactly the
// connections most Kashat customers browse on.
//
// Gatsby raises `onRouteUpdateDelayed` when a navigation passes its threshold,
// and `onRouteUpdate` when the page finally renders, so the honest fix is to
// show that something is happening. Written by hand rather than pulling in
// nprogress: it is a div and a keyframe, and the site already carries enough
// dependencies that no longer resolve.

const BAR_ID = "kclr-route-progress";
const STYLE_ID = "kclr-route-progress-style";

const css = `
@keyframes kclr-route-progress-grow {
  0%   { transform: scaleX(0); }
  40%  { transform: scaleX(0.55); }
  80%  { transform: scaleX(0.85); }
  100% { transform: scaleX(0.95); }
}
#${BAR_ID} {
  position: fixed; inset-block-start: 0; inset-inline-start: 0;
  z-index: 2147483647; height: 3px; width: 100%;
  background: #F6B904; transform-origin: 0 50%; transform: scaleX(0);
  animation: kclr-route-progress-grow 8s cubic-bezier(.1,.7,.2,1) forwards;
  pointer-events: none;
}
#${BAR_ID}[data-done="true"] {
  animation: none; transform: scaleX(1);
  transition: transform .12s ease-out, opacity .25s ease-out .1s; opacity: 0;
}
/* Someone who has asked for less motion still needs to know it is working. */
@media (prefers-reduced-motion: reduce) {
  #${BAR_ID} { animation: none; transform: scaleX(0.4); }
}
`;

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
}

export function startRouteProgress() {
  if (typeof document === "undefined") return;
  ensureStyle();
  let bar = document.getElementById(BAR_ID);
  if (bar) bar.remove();            // restart cleanly if a second navigation begins
  bar = document.createElement("div");
  bar.id = BAR_ID;
  bar.setAttribute("role", "progressbar");
  bar.setAttribute("aria-label", document.documentElement.lang === "ar" ? "جاري التحميل" : "Loading");
  document.body.appendChild(bar);
}

export function finishRouteProgress() {
  if (typeof document === "undefined") return;
  const bar = document.getElementById(BAR_ID);
  if (!bar) return;
  bar.dataset.done = "true";
  // Leave it long enough to read as "finished" rather than vanishing mid-way.
  window.setTimeout(() => bar.remove(), 400);
}
